<script setup>
/*
------------------------------------------------------------------------
Base Confirm Dialog Component

A centered confirmation dialog for destructive actions.
Provides consistent behavior:
- Escape key to cancel
- Focus trap within dialog
- Backdrop click to cancel (optional)
- Fade transition animation

@prop {boolean} show - Whether the dialog is visible
@prop {string} title - Dialog title
@prop {string} message - Dialog message (can contain HTML)
@prop {string} confirmLabel - Label for confirm button (default: 'Confirm')
@prop {string} cancelLabel - Label for cancel button (default: 'Cancel')
@prop {string} confirmClass - Additional classes for confirm button (default: 'bg-red-600 hover:bg-red-700')
@emit confirm - Emitted when confirm button is clicked
@emit cancel - Emitted when cancel button is clicked or dialog is dismissed
------------------------------------------------------------------------
*/
import { onMounted, onUnmounted, watch, nextTick, ref } from "vue";

const props = defineProps({
	show: {
		type: Boolean,
		default: false,
	},
	title: {
		type: String,
		default: "Confirm",
	},
	message: {
		type: String,
		default: "Are you sure?",
	},
	confirmLabel: {
		type: String,
		default: "Confirm",
	},
	cancelLabel: {
		type: String,
		default: "Cancel",
	},
	confirmClass: {
		type: String,
		default: "",
	},
});

const emit = defineEmits(["confirm", "cancel"]);

const dialogRef = ref(null);
const cancelButtonRef = ref(null);

// Handle Escape key
const handleKeydown = (event) => {
	if (event.key === "Escape" && props.show) {
		emit("cancel");
	}
};

// Focus trap - keep focus within dialog
const handleFocusTrap = (event) => {
	if (!props.show || !dialogRef.value) return;

	const focusableElements = dialogRef.value.querySelectorAll(
		'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
	);

	if (focusableElements.length === 0) return;

	const firstElement = focusableElements[0];
	const lastElement = focusableElements[focusableElements.length - 1];

	if (event.shiftKey && document.activeElement === firstElement) {
		event.preventDefault();
		lastElement.focus();
	} else if (!event.shiftKey && document.activeElement === lastElement) {
		event.preventDefault();
		firstElement.focus();
	}
};

// Handle Tab key for focus trap
const handleTab = (event) => {
	if (event.key === "Tab" && props.show) {
		handleFocusTrap(event);
	}
};

// Watch for show changes to manage body scroll and focus
watch(
	() => props.show,
	(newValue) => {
		if (newValue) {
			// Set scrollbar width CSS variable to prevent page jump when hiding scrollbar
			const scrollbarWidth =
				window.innerWidth - document.documentElement.clientWidth;
			document.body.style.setProperty(
				"--scrollbar-width",
				`${scrollbarWidth}px`,
			);
			document.body.classList.add("no-scroll");
			// Focus the cancel button when dialog opens
			nextTick(() => {
				cancelButtonRef.value?.focus();
			});
		} else {
			document.body.classList.remove("no-scroll");
			document.body.style.removeProperty("--scrollbar-width");
		}
	},
);

onMounted(() => {
	window.addEventListener("keydown", handleKeydown);
	window.addEventListener("keydown", handleTab);
});

onUnmounted(() => {
	window.removeEventListener("keydown", handleKeydown);
	window.removeEventListener("keydown", handleTab);
	document.body.classList.remove("no-scroll");
	document.body.style.removeProperty("--scrollbar-width");
});

const handleConfirm = () => {
	emit("confirm");
};

const handleCancel = () => {
	emit("cancel");
};

const handleBackdropClick = () => {
	emit("cancel");
};
</script>

<template>
	<Teleport to="body">
		<transition name="stepfade">
			<div
				v-if="show"
				class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80"
			>
				<div
					ref="dialogRef"
					class="mx-4 max-w-md bg-white p-6 text-black shadow-lg"
					role="dialog"
					aria-modal="true"
					:aria-labelledby="title"
				>
					<h3 class="mb-4 text-lg font-bold text-black">{{ title }}</h3>
					<p class="mb-6 text-black" v-html="message"></p>
					<div class="flex justify-end gap-4">
						<button
							ref="cancelButtonRef"
							type="button"
							@click="handleCancel"
							class="lux-btn lux-btn-outline lux-btn-small transition-colors"
						>
							{{ cancelLabel }}
						</button>
						<button
							type="button"
							@click="handleConfirm"
							class="lux-btn lux-btn-black lux-btn-small transition-colors"
							:class="confirmClass"
						>
							{{ confirmLabel }}
						</button>
					</div>
				</div>
			</div>
		</transition>
	</Teleport>
</template>
